import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabaseStorageService } from '../services/supabaseStorage';
import { v4 as uuidv4 } from 'uuid';

const { FiX, FiFile, FiFolder, FiLock, FiUpload, FiDownload, FiTrash2 } = FiIcons;

export default function DocumentModal({ doc, onClose, onSave }) {
  const [formData, setFormData] = useState({
    id: '',
    title: '',
    category: 'Allgemein',
    expiryDate: '',
    fileUrl: '',
    filePath: '',
    fileName: '',
    fileSize: '',
    isProtected: false,
    notes: ''
  });

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    if (doc) {
      setFormData({
        id: doc.id || uuidv4(),
        title: doc.title || '',
        category: doc.category || 'Allgemein',
        expiryDate: doc.expiryDate ? doc.expiryDate.split('T')[0] : '',
        fileUrl: doc.fileUrl || '',
        filePath: doc.filePath || '',
        fileName: doc.fileName || '',
        fileSize: doc.fileSize || '',
        isProtected: doc.isProtected || false,
        notes: doc.notes || ''
      });
    } else {
      // Neue Datei - ID generieren
      setFormData(prev => ({
        ...prev,
        id: uuidv4()
      }));
    }
  }, [doc]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      alert('Bitte geben Sie einen Titel ein');
      return;
    }

    // Payload mit allen Feldern
    const payload = {
      id: formData.id,
      title: formData.title,
      category: formData.category,
      expiryDate: formData.expiryDate ? new Date(formData.expiryDate).toISOString() : null,
      fileUrl: formData.fileUrl,
      filePath: formData.filePath,
      fileName: formData.fileName,
      fileSize: formData.fileSize,
      isProtected: formData.isProtected,
      notes: formData.notes
    };

    console.log('Saving document:', payload);
    onSave(payload);
    onClose();
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Größenlimit: 10MB
    if (file.size > 10 * 1024 * 1024) {
      alert('Datei zu groß! Maximale Größe: 10MB');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Simuliere Progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + Math.random() * 30, 90));
      }, 200);

      const result = await supabaseStorageService.uploadFile(file, 'documents');
      
      clearInterval(progressInterval);
      setUploadProgress(100);

      console.log('Upload result:', result);
      
      setFormData(prev => ({
        ...prev,
        fileUrl: result.publicUrl,
        filePath: result.filePath,
        fileName: result.fileName,
        fileSize: result.fileSize
      }));

      // Nach 1 Sekunde Progress zurücksetzen
      setTimeout(() => setUploadProgress(0), 1000);
    } catch (error) {
      console.error('Upload error:', error);
      alert(`Upload fehlgeschlagen: ${error.message}`);
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = async () => {
    if (!formData.filePath) return;

    if (confirm('Möchten Sie die Datei wirklich entfernen?')) {
      try {
        await supabaseStorageService.deleteFile(formData.filePath);
        setFormData(prev => ({
          ...prev,
          fileUrl: '',
          filePath: '',
          fileName: '',
          fileSize: ''
        }));
      } catch (error) {
        console.error('Delete error:', error);
        alert(`Löschen fehlgeschlagen: ${error.message}`);
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-30" onClick={onClose} />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {doc ? 'Dokument bearbeiten' : 'Neues Dokument'}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titel *</label>
              <div className="relative">
                <SafeIcon icon={FiFile} className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  className="w-full pl-10 border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="z.B. Versicherungspolice"
                  required
                />
              </div>
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kategorie</label>
              <div className="relative">
                <SafeIcon icon={FiFolder} className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <select
                  value={formData.category}
                  onChange={e => setFormData({ ...formData, category: e.target.value })}
                  className="w-full pl-10 border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="Allgemein">Allgemein</option>
                  <option value="Versicherungen">Versicherungen</option>
                  <option value="Finanzen">Finanzen</option>
                  <option value="Medizinisch">Medizinisch</option>
                  <option value="Haus">Haus & Wohnen</option>
                  <option value="Fahrzeug">Fahrzeug</option>
                </select>
              </div>
            </div>

            {/* Expiry Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ablaufdatum (optional)</label>
              <input
                type="date"
                value={formData.expiryDate}
                onChange={e => setFormData({ ...formData, expiryDate: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>

            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Datei</label>
              
              {formData.fileUrl ? (
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <SafeIcon icon={FiFile} className="w-5 h-5 text-blue-600 mr-2" />
                      <div>
                        <p className="font-medium text-sm text-gray-900 truncate max-w-[200px]">
                          {formData.fileName}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(formData.fileSize)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveFile}
                      className="text-red-600 hover:text-red-800 p-1"
                    >
                      <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    </button>
                  </div>
                  <a
                    href={formData.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                  >
                    <SafeIcon icon={FiDownload} className="w-4 h-4 mr-1" />
                    Datei ansehen
                  </a>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                  <label className="cursor-pointer block">
                    <SafeIcon icon={FiUpload} className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-600">
                      Datei hochladen (max. 10MB)
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      PDF, JPG, PNG oder DOCX
                    </p>
                    <input
                      type="file"
                      onChange={handleFileUpload}
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.txt"
                      disabled={uploading}
                    />
                  </label>
                  
                  {uploading && (
                    <div className="mt-4">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Wird hochgeladen... {uploadProgress}%
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Protected Document */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isProtected"
                checked={formData.isProtected}
                onChange={e => setFormData({ ...formData, isProtected: e.target.checked })}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <label htmlFor="isProtected" className="text-sm text-gray-700 flex items-center">
                <SafeIcon icon={FiLock} className="w-3 h-3 mr-1" />
                Sensibles Dokument (Passwortschutz simulieren)
              </label>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notizen</label>
              <textarea
                value={formData.notes}
                onChange={e => setFormData({ ...formData, notes: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 h-20"
                placeholder="Zusätzliche Informationen zum Dokument..."
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                disabled={uploading}
              >
                {uploading ? 'Wird hochgeladen...' : 'Speichern'}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
}